if (!customElements.get('molecule-scroll')) {
  class MoleculeScroll extends HTMLElement {
    constructor() {
      super();

      this.slides = Array.from(this.querySelectorAll('[data-molecule-slide]'));
      this.video = this.querySelector('video');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = -1;
      this.isVisible = false;
      this.cycleTimer = null;

      this.onMotionChange = this.onMotionChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.prepareTextAnimation();

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onMotionChange);
      } else {
        this.reducedMotion.addListener(this.onMotionChange);
      }

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.classList.toggle('is-visible', this.isVisible);
          this.updatePlayback();
          this.updateCycle();
        },
        { rootMargin: '0px', threshold: 0 }
      );
      this.observer.observe(this);

      this.onMotionChange();
    }

    prepareTextAnimation() {
      const segmenter =
        typeof Intl.Segmenter === 'function'
          ? new Intl.Segmenter(document.documentElement.lang || 'pl', { granularity: 'grapheme' })
          : null;
      const splitCharacters = (value) =>
        segmenter ? Array.from(segmenter.segment(value), ({ segment }) => segment) : Array.from(value);

      this.slides.forEach((slide) => {
        const lines = Array.from(slide.querySelectorAll('.molecule-scroll__line'));

        lines.forEach((line, lineIndex) => {
          if (line.dataset.moleculeCharacters === 'true') return;

          const text = line.textContent.replace(/\s+/g, ' ').trim();
          if (!text) return;

          const parts = text.split(/(\s+)/u).map((part) => ({
            isSpace: /^\s+$/u.test(part),
            characters: /^\s+$/u.test(part) ? [] : splitCharacters(part),
          }));
          const characterCount = parts.reduce((count, part) => count + part.characters.length, 0);
          const center = (characterCount - 1) / 2;
          const closestDistance = characterCount % 2 === 0 ? 0.5 : 0;
          const fragment = document.createDocumentFragment();
          const accessibleText = document.createElement('span');
          const animatedLine = document.createElement('span');
          let characterIndex = 0;

          accessibleText.className = 'visually-hidden';
          accessibleText.textContent = text;
          animatedLine.className = 'molecule-scroll__animated-line';
          animatedLine.setAttribute('aria-hidden', 'true');

          parts.forEach((part) => {
            if (part.isSpace) {
              animatedLine.append(document.createTextNode(' '));
              return;
            }

            const word = document.createElement('span');
            word.className = 'molecule-scroll__word';

            part.characters.forEach((character) => {
              const characterElement = document.createElement('span');
              const distanceFromCenter = Math.max(Math.abs(characterIndex - center) - closestDistance, 0);
              const delay = lineIndex * 180 + Math.round(distanceFromCenter * 24);

              characterElement.className = 'molecule-scroll__character';
              characterElement.textContent = character;
              characterElement.style.setProperty('--molecule-character-delay', `${delay}ms`);
              word.append(characterElement);
              characterIndex += 1;
            });

            animatedLine.append(word);
          });

          fragment.append(accessibleText, animatedLine);
          line.replaceChildren(fragment);
          line.dataset.moleculeCharacters = 'true';
        });
      });

      this.classList.add('is-characterized');
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.onMotionChange);
      } else {
        this.reducedMotion.removeListener(this.onMotionChange);
      }

      this.observer?.disconnect();
      this.stopCycle();
    }

    onMotionChange() {
      if (this.reducedMotion.matches) {
        this.slides.forEach((slide) => {
          slide.classList.remove('is-before', 'is-after');
          slide.classList.add('is-active');
          slide.removeAttribute('aria-hidden');
        });
      } else {
        this.setActiveSlide(this.activeIndex < 0 ? 0 : this.activeIndex, true);
      }

      this.updatePlayback();
      this.updateCycle();
    }

    onVisibilityChange() {
      this.updatePlayback();
      this.updateCycle();
    }

    onBlockSelect(event) {
      const selectedSlide = event.target.closest?.('[data-molecule-slide]');
      const selectedIndex = this.slides.indexOf(selectedSlide);

      if (selectedIndex < 0) return;

      this.editorIndex = selectedIndex;
      this.setActiveSlide(selectedIndex, true);
      this.updateCycle();
    }

    onBlockDeselect() {
      this.editorIndex = undefined;
      this.updateCycle();
    }

    setActiveSlide(index, force = false) {
      if (!this.slides.length || (!force && index === this.activeIndex)) return;

      this.activeIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-before', slideIndex < this.activeIndex);
        slide.classList.toggle('is-after', slideIndex > this.activeIndex);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    updateCycle() {
      this.stopCycle();

      if (
        this.slides.length < 2 ||
        !this.isVisible ||
        document.hidden ||
        this.reducedMotion.matches ||
        this.editorIndex !== undefined
      ) {
        return;
      }

      const interval = Math.max(Number(this.dataset.cycleInterval) || 2400, 100);
      this.cycleTimer = window.setInterval(() => {
        this.setActiveSlide(this.activeIndex + 1);
      }, interval);
    }

    stopCycle() {
      if (this.cycleTimer === null) return;

      window.clearInterval(this.cycleTimer);
      this.cycleTimer = null;
    }

    updatePlayback() {
      if (!this.video) return;

      this.video.muted = true;

      if (this.isVisible && !document.hidden && !this.reducedMotion.matches) {
        const playPromise = this.video.play();
        playPromise?.catch(() => {});
      } else {
        this.video.pause();
      }
    }
  }

  customElements.define('molecule-scroll', MoleculeScroll);
}
